import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { getDeviceId } from "../../lib/storage";
import type { GroupState } from "./GroupParts";

export const REACTIONS: { id: string; emoji: string; label: string }[] = [
  { id: "fire", emoji: "🔥", label: "Fire" }, { id: "laugh", emoji: "😂", label: "Haha" }, { id: "love", emoji: "😍", label: "Love" },
  { id: "shock", emoji: "😱", label: "Shock" }, { id: "thumbs", emoji: "👍", label: "Yes" }, { id: "party", emoji: "🎉", label: "Party" },
];
const emojiOf = (id: string) => REACTIONS.find((r) => r.id === id)?.emoji || "✨";

export interface FloatingReaction { key: number; reaction: string; name: string; x: number }

export function ReactionBar({ group }: { group: GroupState }) {
  const [cool, setCool] = useState(false);
  const send = async (reaction: string) => {
    if (!group.me || cool) return;
    setCool(true);
    setTimeout(() => setCool(false), 500);
    try {
      await api.post(`/groups/${group.id}/react`, { participant_id: group.me.id, device_id: getDeviceId(), reaction });
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <div className="flex items-center justify-between gap-1 rounded-2xl border-[3px] border-ink bg-white p-2 pop-shadow-sm" data-testid="group-reaction-bar">
      {REACTIONS.map((r) => (
        <button key={r.id} onClick={() => send(r.id)} disabled={cool} aria-label={`React ${r.label}`} data-testid={`group-react-${r.id}`}
          className="flex-1 rounded-xl py-2 text-2xl transition-transform hover:-translate-y-1 hover:bg-pop-yellow/50 active:scale-75 disabled:opacity-60">
          {r.emoji}
        </button>
      ))}
    </div>
  );
}

export function FloatingReactions({ items }: { items: FloatingReaction[] }) {
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-20 z-[65] h-0 md:bottom-6" aria-hidden data-testid="group-floating-reactions">
      <AnimatePresence>
        {items.map((r) => (
          <motion.div key={r.key} className="absolute bottom-0 flex flex-col items-center" style={{ left: `${r.x}%` }}
            initial={{ y: 0, opacity: 0, scale: 0.4 }} animate={{ y: -320, opacity: [0, 1, 1, 0], scale: [0.4, 1.3, 1, 1], rotate: [0, -12, 10, 0] }}
            exit={{ opacity: 0 }} transition={{ duration: 1.8, ease: "easeOut" }} data-testid="group-floating-reaction">
            <span className="text-5xl drop-shadow-[3px_3px_0_#121212]">{emojiOf(r.reaction)}</span>
            <span className="mt-1 max-w-[8rem] truncate rounded-full border-2 border-ink bg-white px-2 text-[11px] font-extrabold">{r.name}</span>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
